import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGoals } from '../contexts/GoalContext';
import { useOKR } from '../contexts/OKRContext';
import { usePerformance } from '../contexts/PerformanceContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bell, 
  Settings, 
  User, 
  LogOut, 
  Menu,
  X,
  ChevronDown,
  LayoutDashboard,
  Target,
  Rocket,
  FileText,
  BarChart3,
  Users,
  Calendar
} from 'lucide-react';

// Import new components
import DashboardWidgets from './DashboardWidgets';
import QuickActions from './QuickActions';
import ActivityTimeline from './ActivityTimeline';
import AchievementBadges from './AchievementBadges';
import PerformanceCharts from './PerformanceCharts';
import ToastNotifications from './ToastNotifications';

const EnhancedDashboard = () => {
  const { user, logout } = useAuth();
  const { analytics: goalAnalytics } = useGoals();
  const { analytics: okrAnalytics } = useOKR();
  const { analytics: performanceAnalytics } = usePerformance();
  const { getUnreadCount, notifications } = useNotifications();
  const navigate = useNavigate();

  const [activeView, setActiveView] = useState('overview');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigationItems = [
    {
      id: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      description: 'Dashboard overview with widgets'
    },
    {
      id: 'quick-actions',
      label: 'Quick Actions',
      icon: Target,
      description: 'Fast access to common tasks'
    },
    {
      id: 'goals',
      label: 'Goals',
      icon: Target,
      action: () => navigate('/goals'),
      description: 'Manage your goals'
    },
    {
      id: 'okrs',
      label: 'OKRs',
      icon: Rocket,
      action: () => navigate('/okrs'),
      description: 'Objectives & Key Results'
    },
    {
      id: 'reviews',
      label: 'Reviews',
      icon: FileText,
      action: () => navigate('/performance-reviews'),
      description: 'Performance reviews'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      description: 'Performance insights and charts'
    },
    {
      id: 'advanced-analytics',
      label: 'Advanced Analytics',
      icon: BarChart3,
      action: () => navigate('/advanced-analytics'),
      description: 'AI insights and custom reports'
    },
    {
      id: 'activity',
      label: 'Activity',
      icon: Users,
      description: 'Recent activity timeline'
    },
    {
      id: 'achievements',
      label: 'Achievements',
      icon: Target,
      description: 'Badges and accomplishments'
    }
  ];

  // Filter navigation based on user role
  const filteredNavigation = navigationItems.filter(item => {
    if (item.id === 'analytics' && !['manager', 'hr', 'executive', 'admin'].includes(user?.role)) {
      return false;
    }
    if (item.id === 'advanced-analytics' && !['manager', 'hr', 'executive', 'admin'].includes(user?.role)) {
      return false;
    }
    return true;
  });

  const unreadCount = getUnreadCount();

  const renderContent = () => {
    switch (activeView) {
      case 'overview':
        return <DashboardWidgets />;
      case 'quick-actions':
        return <QuickActions />;
      case 'analytics':
        return (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Performance Analytics</h2>
              <p className="text-gray-600">Detailed insights into your performance data</p>
            </div>
            <PerformanceCharts 
              goalAnalytics={goalAnalytics}
              okrAnalytics={okrAnalytics}
              performanceAnalytics={performanceAnalytics}
              userRole={user?.role}
            />
          </div>
        );
      case 'activity':
        return (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Activity Timeline</h2>
              <p className="text-gray-600">Recent updates and activities across the platform</p>
            </div>
            <ActivityTimeline limit={20} />
          </div>
        );
      case 'achievements':
        return (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Achievements & Badges</h2>
              <p className="text-gray-600">Track your accomplishments and progress streaks</p>
            </div>
            <AchievementBadges />
          </div>
        );
      default:
        return <DashboardWidgets />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notifications */}
      <ToastNotifications />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Left side */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              
              <div>
                <h1 className="text-xl font-bold text-gray-900">Performance Dashboard</h1>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Welcome back, {user?.first_name}!
                </p>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">Notifications</h3>
                        <span className="text-sm text-gray-600">{unreadCount} unread</span>
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.slice(0, 5).map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 border-b border-gray-200 hover:bg-gray-50 ${
                            !notification.read ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <span className="text-lg">{notification.icon}</span>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {notification.title}
                              </p>
                              <p className="text-sm text-gray-600">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(notification.timestamp).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-4 border-t border-gray-200">
                      <button className="text-sm text-blue-600 hover:text-blue-800">
                        View all notifications
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                >
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                  <ChevronDown size={16} />
                </button>

                {/* User Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-xs text-gray-600">{user?.email}</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                        {user?.role?.toUpperCase()}
                      </span>
                    </div>
                    <div className="py-2">
                      <button
                        onClick={() => navigate('/profile')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <User size={16} className="mr-2" />
                        Profile
                      </button>
                      <button
                        onClick={() => {/* Settings action */}}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Settings size={16} className="mr-2" />
                        Settings
                      </button>
                      <hr className="my-1" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut size={16} className="mr-2" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white shadow-lg border-r border-gray-200 transition-transform duration-300 ease-in-out`}>
          <div className="p-6">
            <nav className="space-y-2">
              {filteredNavigation.map((item) => {
                const IconComponent = item.icon;
                const isActive = activeView === item.id;
                
                return (
                  <motion.button
                    key={item.id}
                    onClick={() => {
                      if (item.action) {
                        item.action();
                      } else {
                        setActiveView(item.id);
                      }
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      isActive
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <IconComponent size={18} />
                    <div>
                      <div className="font-medium">{item.label}</div>
                      <div className="text-xs text-gray-500">{item.description}</div>
                    </div>
                  </motion.button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <motion.div
            key={activeView}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="min-h-screen"
          >
            {renderContent()}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default EnhancedDashboard;